import {
  Box,
  Card,
  Grid,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Button from "@mui/material/Button";
import { Cancel, ThumbUp, ThumbDown } from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import {
  Permit,
  PermitTransactionStatus,
} from "types";
import { forwardRef, ReactNode, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { cardHeaderSx, cardSx, mobileCardSx } from "utils/styleProps";
import { ValidatorForm } from "react-material-ui-form-validator";
import AuturaTextField from "components/inputs/AuturaTextField";
import { formatCurrency, formatVehicleString } from "utils/stringFormatters";
import dayjs from "dayjs";
import { TransitionProps } from "@mui/material/transitions";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});
interface PermitReviewDialogProps {
  isOpen: boolean;
  handleCancel: () => void;
  handlePermitApproval: (permit: Permit) => void;
  handlePermitDenial: (permit: Permit) => void;
  permit: Permit;
}

const PermitReviewDialog = ({
  isOpen,
  handleCancel,
  handlePermitApproval,
  handlePermitDenial,
  permit,
}: PermitReviewDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("lg"));

  const vehicleString = useMemo(() => {
    const str = formatVehicleString(permit?.vehicle);
    return str;
  }, [permit?.vehicle]);

  const pendingTransaction: string = useMemo(() => {
    let str = "";
    if (!permit?.transactions || permit?.transactions.length === 0) return str;
    const trans = permit?.transactions.find(
      (t) => t.status === PermitTransactionStatus.HOLD,
    );
    if (trans) {
      str = t("dialogs.approval.hold_text", {
        ns: "permits",
        amount: formatCurrency(trans.data?.details?.amount || 0),
        brand: trans.data?.paymentMethodDetails?.brand || "card",
        last4: trans.data?.paymentMethodDetails?.lastFour || "****",
      });
    }
    return str;
  }, [permit?.transactions]);

  const timeLeft: string = useMemo(() => {
    if (!permit.created) return "";

    // 5 days from renewal date
    if (permit.data.renewalDate) {
      const renewalDate = new Date(permit.data.renewalDate);
      return dayjs(renewalDate).format("MM-DD-YYYY hh:mm a");
    }
    // add 5 days to created
    const created = new Date(permit?.created);
    const endTime = new Date(created.getTime() + 5 * 24 * 60 * 60 * 1000);
    return dayjs(endTime).format("MM-DD-YYYY hh:mm a");
  }, [permit?.endTime]);

  const permitTypeLabel = useMemo(() => {
    const name = permit?.permitType.name;
    if (permit?.permitType.permitPricing?.pricePerUnit) {
      return `${name} - ${formatCurrency(
        permit?.permitType.permitPricing.pricePerUnit,
      )}`;
    }
    return name;
  }, [permit?.permitType]);

  const handlePermitApprovalDecision = (approved: boolean) => {
    if (approved) {
      handlePermitApproval(permit);
    } else {
      handlePermitDenial(permit);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      aria-label={`${t("permit.invite.header")} Dialog`}
      TransitionComponent={Transition}
      fullScreen={isMobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <ValidatorForm onSubmit={() => ({})} instantValidate={false}>
        <Card sx={isMobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              isMobile
                ? {
                    display: "flex",
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                    justifyContent: "space-between",
                    flexDirection: "row",
                    alignItems: "center",
                    width: "100%",
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, alignItems: "center", display: "flex" }}>
              <Typography
                variant={"h4"}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: "left",
                }}
              >
                {t("dialogs.approval.review", { ns: "permits" })}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                alignItems: "center",
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button
                variant={"text"}
                onClick={handleCancel}
                sx={{
                  px: 1,
                  textTransform: "uppercase",
                  color: palette.secondary.contrastText,
                }}
                endIcon={
                  <Cancel
                    htmlColor={palette.secondary.contrastText}
                    fontSize={"large"}
                  />
                }
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container width={"100%"} spacing={1.5}>
              <Grid size={12}>
                <AuturaTextField
                  label={t("permit_type.label", { ns: "entities" })}
                  name={"permit_type.label"}
                  value={permitTypeLabel}
                  readonly
                />
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  label={t("permit.contact.name", { ns: "entities" })}
                  name={"permit.contact.name"}
                  value={permit?.contact?.name || ""}
                  readonly
                />
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  name="permit.contact.primaryEmail"
                  label={t("permit.contact.primaryEmail", { ns: "entities" })}
                  initialValue="email"
                  value={permit?.contact?.primaryEmail || ""}
                  readonly
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t("permit.vehicle.plate.number", { ns: "entities" })}
                  name={"permit.vehicle.plate.number"}
                  value={permit?.vehicle?.plate?.number || ""}
                  readonly
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t("permit.vehicle.plate.state", { ns: "entities" })}
                  name={"permit.vehicle.plate.state"}
                  value={permit?.vehicle?.plate?.state || ""}
                  readonly
                />
              </Grid>
              {vehicleString && (
                <Grid size={12}>
                  <AuturaTextField
                    label={t("permit.vehicle.label", { ns: "entities" })}
                    name={"permit.vehicle.label"}
                    value={vehicleString.toUpperCase()}
                    readonly
                  />
                </Grid>
              )}
              {pendingTransaction && (
                <Grid size={12}>
                  <AuturaTextField
                    name="pendingTransaction"
                    label={t("dialogs.approval.pending_transaction", {
                      ns: "permits",
                    })}
                    value={pendingTransaction}
                    readonly
                  />
                </Grid>
              )}
              <Grid size={12}>
                <Typography variant="body2" align="center">
                  {t("dialogs.approval.time_left", {
                    ns: "permits",
                    date: timeLeft,
                  })}
                </Typography>
              </Grid>
              <Grid mt={2} size={12}>
                <Stack
                  width="100%"
                  direction="row"
                  spacing={2}
                  justifyContent={{ xs: "space-between", lg: "flex-end" }}
                  display={{ xs: "none", md: "flex" }}
                  alignItems="flex-end"
                  alignContent="flex-end"
                >
                  <Button
                    sx={{
                      flex: { xs: 1, lg: "none" },
                      borderRadius: "8px",
                      px: 2,
                      py: 1.25,
                    }}
                    variant={"outlined"}
                    onClick={() => handlePermitApprovalDecision(false)}
                    color={"secondary"}
                    startIcon={<ThumbDown />}
                  >
                    {t("dialogs.approval.deny", { ns: "permits" })}
                  </Button>
                  <Button
                    sx={{
                      flex: { xs: 1, lg: "none" },
                      borderRadius: "8px",
                      px: 2,
                      py: 1.25,
                    }}
                    variant={"contained"}
                    color={"secondary"}
                    endIcon={<ThumbUp />}
                    onClick={() => handlePermitApprovalDecision(true)}
                  >
                    {t("dialogs.approval.approve", { ns: "permits" })}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};
export default PermitReviewDialog;
